import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../../api/client';
import { useUIStore } from '../../../stores/uiStore';
import { useSSEStore } from '../../../stores/sseStore';
import { usePagination } from '../../../hooks/usePagination';
import { Pagination } from '../../../components/shared/Pagination';
import { Loader2, AlertCircle } from 'lucide-react';
import { DraftDetailModal } from '../modals/DraftDetailModal';
import { useCan } from '../../../lib/rbac';
import { FamiliesView } from '../components/FamiliesView';

function PublishedTab() {
  const { t } = useTranslation();
  const canMoney = useCan()('global.view_money');
  const { sellerId } = useUIStore();
  const { subscribe } = useSSEStore();
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const pag = usePagination('pub-published');
  const [selectedDraft, setSelectedDraft] = useState<any>(null);
  const [siteErrors, setSiteErrors] = useState(false);
  // Vista "por familia" (2026-10-22): mismo permiso que esta pestaña, solo lectura.
  const [vista, setVista] = useState<'avisos' | 'familias'>('avisos');

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ seller_id: String(sellerId), page: String(pag.page), limit: String(pag.perPage) });
      if (siteErrors) params.set('site_errors', 'true');
      const res = await api.get<any>(`/publisher/published?${params}`);
      if (res.success && res.data) {
        setData(res.data.items || []);
        pag.setTotal(res.data.total || 0);
      }
    } catch (err) {
      console.error('Failed to load published:', err);
    } finally {
      setLoading(false);
    }
  }, [sellerId, pag.page, pag.perPage, siteErrors]);

  useEffect(() => { loadData(); }, [loadData]);

  const loadRef = useRef(loadData);
  loadRef.current = loadData;
  useEffect(() => {
    const unsub1 = subscribe('draft:published', () => loadRef.current());
    const unsub2 = subscribe('site_item:synced', () => loadRef.current());
    return () => { unsub1(); unsub2(); };
  }, [subscribe]);

  const selectorVista = (
    <div className="inline-flex rounded-lg border border-[var(--color-border)] overflow-hidden">
      {(['avisos', 'familias'] as const).map((v) => (
        <button key={v} onClick={() => setVista(v)}
          className={`px-3 py-1.5 text-xs font-medium transition-colors ${
            vista === v ? 'bg-[var(--color-primary)] text-white' : 'bg-[var(--color-bg-tertiary)] text-[var(--color-text-secondary)] hover:text-[var(--color-text)]'
          }`}>
          {v === 'avisos' ? t('pub.families.viewListings') : t('pub.families.viewFamilies')}
        </button>
      ))}
    </div>
  );

  if (vista === 'familias') {
    return (
      <div className="space-y-4">
        {selectorVista}
        <FamiliesView />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {selectorVista}
      <div className="flex items-center justify-between">
        <span className="text-sm text-[var(--color-text-secondary)]">{Number(pag.total).toLocaleString()} published items</span>
        <button
          onClick={() => { setSiteErrors(!siteErrors); pag.resetPage(); }}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
            siteErrors
              ? 'bg-red-500/20 text-red-400 border-red-500/40'
              : 'bg-[var(--color-bg-tertiary)] text-[var(--color-text-secondary)] border-[var(--color-border)] hover:text-[var(--color-text)]'
          }`}
        >
          <AlertCircle size={14} />
          Site Errors
        </button>
      </div>

      <div className="bg-[var(--color-bg-secondary)] rounded-lg border border-[var(--color-border)] overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[var(--color-border)]">
              <th className="text-left px-3 py-2.5 text-[var(--color-text-secondary)] font-medium text-xs">ASIN</th>
              <th className="text-left px-3 py-2.5 text-[var(--color-text-secondary)] font-medium text-xs">Title</th>
              <th className="text-left px-3 py-2.5 text-[var(--color-text-secondary)] font-medium text-xs">CBT ID</th>
              <th className="text-right px-3 py-2.5 text-[var(--color-text-secondary)] font-medium text-xs">Price</th>
              <th className="text-center px-3 py-2.5 text-[var(--color-text-secondary)] font-medium text-xs">Sites</th>
              <th className="text-left px-3 py-2.5 text-[var(--color-text-secondary)] font-medium text-xs">Published</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="text-center py-12"><Loader2 className="w-5 h-5 animate-spin mx-auto text-[var(--color-primary)]" /></td></tr>
            ) : data.length === 0 ? (
              <tr><td colSpan={6} className="text-center py-12 text-[var(--color-text-tertiary)]">No published items</td></tr>
            ) : data.map((row) => {
              const sites: any[] = row.site_items || [];
              const liveCount = sites.filter((s: any) => s.status === 'active' || s.status === 'paused').length;
              const errorCount = sites.filter((s: any) => s.status === 'error' || (!s.site_item_id && s.status !== 'active' && s.status !== 'paused')).length;
              return (
                <tr key={row.draft_id} onClick={() => setSelectedDraft({ ...row, id: row.draft_id, status: 'published' })}
                  className="border-b border-[var(--color-border)] hover:bg-[var(--color-bg-tertiary)] cursor-pointer transition-colors">
                  <td className="px-3 py-2.5"><span className="font-mono text-xs text-blue-400">{row.asin || '—'}</span></td>
                  <td className="px-3 py-2.5 text-[var(--color-text)] text-xs max-w-[250px]">
                    <span className="block" title={row.title_seo}>{row.title_seo || '—'}</span>
                  </td>
                  <td className="px-3 py-2.5">
                    {row.cbt_id ? <span className="font-mono text-xs text-[var(--color-text-secondary)]">{row.cbt_id}</span> : '—'}
                  </td>
                  <td className="px-3 py-2.5 text-right font-mono text-xs text-green-400">{canMoney ? `$${Number(row.price || 0).toFixed(2)}` : '\u2014'}</td>
                  <td className="px-3 py-2.5">
                    <div className="flex flex-wrap gap-1 justify-center">
                      {sites.map((si: any) => {
                        const isOk = si.status === 'active' || si.status === 'paused';
                        return (
                          <span key={si.site_id} className={`px-1.5 py-0.5 rounded text-[10px] font-mono border ${
                            isOk ? 'bg-emerald-100 text-emerald-700 border-emerald-300 dark:bg-emerald-900/40 dark:text-emerald-300 dark:border-emerald-600 font-bold' : 'bg-red-100 text-red-700 border-red-300 dark:bg-red-900/40 dark:text-red-300 dark:border-red-600 font-bold'
                          }`} title={isOk ? si.site_item_id : si.error_detail?.cause?.[0]?.message || 'Error'}>
                            {si.site_id}{!isOk && ' !'}
                          </span>
                        );
                      })}
                      {sites.length === 0 && <span className="text-[10px] text-[var(--color-text-tertiary)]">—</span>}
                    </div>
                  </td>
                  <td className="px-3 py-2.5 text-[var(--color-text-secondary)] text-xs whitespace-nowrap">
                    {row.published_at ? new Date(row.published_at).toLocaleDateString() : '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Pagination
        page={pag.page}
        totalPages={pag.totalPages}
        total={pag.total}
        perPage={pag.perPage}
        onPageChange={pag.setPage}
        onPerPageChange={pag.setPerPage}
      />

      {selectedDraft && (
        <DraftDetailModal draft={selectedDraft} onClose={() => setSelectedDraft(null)} onUpdate={loadData} sellerId={sellerId} />
      )}
    </div>
  );
}

export { PublishedTab };
